import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, FormGroup, Input, PageHeader, Select } from "@cofano/cck";
import { Box, CircularProgress } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import Helmet from "components/helmet/Helmet";
import ExternalMasterdataAlert, { ExternalMasterdataWarningIcon, MasterdataEntity } from "components/masterdata/ExternalMasterdataAlert";

import { Language } from "imports/Utils";

import {
	invoiceGroupTypeSchema,
	InvoiceGroupTypeSchemaInput,
	InvoiceGroupTypeSchemaOutput,
} from "./utils/invoiceGroupType.schema";

import {
	SaveInvoiceGroupTypeDocument,
} from "__generated__/view/invoicegrouptypes/graphql/invoiceGroupType.mutations.generated";
import {
	GetInvoiceGroupTypeDocument,
} from "__generated__/view/invoicegrouptypes/graphql/invoiceGroupType.queries.generated";
import FormField from "components/cck/FormField";
import { CharterType, DeclarationAutoCreationCondition } from "__generated__/enums";
import CharterTypeSelect from "view/charters/components/selects/CharterTypeSelect";

const InvoiceGroupTypeCreateEdit = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const { control, handleSubmit, reset } = useForm<InvoiceGroupTypeSchemaInput, unknown, InvoiceGroupTypeSchemaOutput>({
		resolver: zodResolver(invoiceGroupTypeSchema),
		defaultValues: {
			id: null,
			name: "",
			declarationAutoCreationCondition: null,
			charterTypes: [ CharterType.Sales, CharterType.Purchase ],
		} satisfies InvoiceGroupTypeSchemaInput,
	});

	const [ saveInvoiceGroupType, { loading } ] = useMutation(SaveInvoiceGroupTypeDocument);

	const { data, loading: savedEntityLoading } = useQuery(GetInvoiceGroupTypeDocument, {
		variables: {
			id: Number(id),
		},
		skip: !id,
		onCompleted: (data) => {
			if (data.invoiceGroupType) {
				reset({
					...data.invoiceGroupType,
					declarationAutoCreationCondition: data.invoiceGroupType.declarationAutoCreationCondition ?? null,
				});
			}
		},
	});

	const save = (data: InvoiceGroupTypeSchemaOutput) => {
		saveInvoiceGroupType({
			variables: {
				input: data,
			},
		}).then(() => {
			navigate("..");
		});
	};

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("INVOICE_GROUP_TYPE")}
					<Helmet title={intl.formatMessage({ id: "INVOICE_GROUP_TYPE" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("..")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading || savedEntityLoading} type="submit">
						{Language("SAVE")}
					</Button>

					<ExternalMasterdataWarningIcon entity={MasterdataEntity.InvoiceGroupType} />
				</PageHeader.Actions>
			</PageHeader>


			{savedEntityLoading ? (
				<Box display="flex" justifyContent="center" alignItems="center">
					<CircularProgress size={24} />
				</Box>
			) : (
				<>
					<ExternalMasterdataAlert
						entity={MasterdataEntity.InvoiceGroupType}
						externalId={data?.invoiceGroupType.externalId}
					/>

					<Card>
						<FormGroup
							label={Language("NAME")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="name"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} autoFocus />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)}
							/>
						</FormGroup>

						<FormGroup
							label={Language("DECLARATION_AUTO_CREATION_CONDITION")}
							labelPlacement="left-center"
							overflow="hidden"
						>
							<Controller
								name="declarationAutoCreationCondition"
								control={control}
								render={({ field, formState }) => (
									<FormField name={field.name} errors={formState.errors}>
										<Select
											value={field.value}
											onChange={field.onChange}
											fetchOptions={Object.values(DeclarationAutoCreationCondition)}
											getOptionLabel={(option) => intl.formatMessage({ id: `DECLARATION_AUTO_CREATION_CONDITION_${option}` })}
											disableSearch
										/>
									</FormField>
								)}
							/>
						</FormGroup>

						<FormGroup
							label={Language("CHARTER_TYPE")}
							labelPlacement="left-center"
							overflow="hidden"
						>
							<Controller
								name="charterTypes"
								control={control}
								render={({ field, formState }) => (
									<FormField name={field.name} errors={formState.errors}>
										<CharterTypeSelect
											{...field}
											clearable={false}
											multiple
										/>
									</FormField>
								)}
							/>
						</FormGroup>
					</Card>
				</>
			)}
		</form>
	);
};

export default InvoiceGroupTypeCreateEdit;
